import React, { useState } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, Send, Loader2 } from "lucide-react";
import { CONTACT } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EJS_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EJS_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function Contacto() {
    const [form, setForm] = useState({
        nombre: "",
        email: "",
        telefono: "",
        mensaje: "",
    });
    const [loading, setLoading] = useState(false);

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim() || !form.email.trim() || !form.mensaje.trim()) {
            toast.error("Completa nombre, email y mensaje.");
            return;
        }
        setLoading(true);
        try {
            // 1. Save to backend (always - acts as backup ledger)
            await axios.post(`${API}/contact`, form);

            // 2. Send via EmailJS if configured (frontend integration)
            if (EJS_SERVICE && EJS_TEMPLATE && EJS_KEY) {
                await emailjs.send(
                    EJS_SERVICE,
                    EJS_TEMPLATE,
                    {
                        from_name: form.nombre,
                        from_email: form.email,
                        phone: form.telefono,
                        message: form.mensaje,
                        to_email: CONTACT.email,
                    },
                    { publicKey: EJS_KEY },
                );
            }

            toast.success("¡Mensaje enviado! Te contactaremos pronto.");
            setForm({ nombre: "", email: "", telefono: "", mensaje: "" });
        } catch (err) {
            console.error(err);
            toast.error("No se pudo enviar tu mensaje. Inténtalo de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contacto"
            data-testid="contacto-section"
            className="bg-navy text-cream relative py-24 md:py-32 px-5 md:px-10 overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute inset-0 halftone-dots-cream opacity-[0.08] pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute -top-32 -right-32 w-96 h-96 sunburst-bg opacity-30"
                style={{ transformOrigin: "50% 50%" }}
            />

            <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
                <div className="fade-up">
                    <div className="font-display uppercase tracking-[0.3em] text-gold text-sm md:text-base mb-3">
                        / Contacto
                    </div>
                    <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl leading-[1.02]">
                        Pongamos tu marca
                        <br />
                        en
                        <span className="text-orange"> órbita.</span>
                    </h2>

                    <p className="mt-6 text-cream/85 text-lg max-w-md font-medium">
                        Cuéntanos tu reto y construimos juntos la próxima
                        campaña que el mundo no podrá ignorar.
                    </p>

                    <ul className="mt-10 space-y-5">
                        <li className="flex items-start gap-4">
                            <span className="border-4 border-cream bg-orange text-navy p-2 mt-1">
                                <MapPin size={20} />
                            </span>
                            <div>
                                <div className="font-display uppercase tracking-widest text-xs text-gold">
                                    Dirección
                                </div>
                                <p
                                    data-testid="contact-address"
                                    className="text-cream/95 font-medium"
                                >
                                    {CONTACT.direccion}
                                </p>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="border-4 border-cream bg-teal p-2 mt-1">
                                <Phone size={20} />
                            </span>
                            <div>
                                <div className="font-display uppercase tracking-widest text-xs text-gold">
                                    Teléfono
                                </div>
                                <a
                                    href={`tel:${CONTACT.telefono}`}
                                    data-testid="contact-phone"
                                    className="text-cream/95 font-medium hover:text-orange"
                                >
                                    {CONTACT.telefono}
                                </a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="border-4 border-cream bg-gold text-navy p-2 mt-1">
                                <Mail size={20} />
                            </span>
                            <div>
                                <div className="font-display uppercase tracking-widest text-xs text-gold">
                                    Email
                                </div>
                                <a
                                    href={`mailto:${CONTACT.email}`}
                                    data-testid="contact-email"
                                    className="text-cream/95 font-medium hover:text-orange break-all"
                                >
                                    {CONTACT.email}
                                </a>
                            </div>
                        </li>
                        <li className="flex items-start gap-4">
                            <span className="border-4 border-cream bg-navy p-2 mt-1">
                                <Globe size={20} />
                            </span>
                            <div>
                                <div className="font-display uppercase tracking-widest text-xs text-gold">
                                    Página Web
                                </div>
                                <span
                                    data-testid="contact-web"
                                    className="text-cream/95 font-medium"
                                >
                                    {CONTACT.web}
                                </span>
                            </div>
                        </li>
                    </ul>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="relative bg-cream text-navy border-[6px] border-cream p-7 md:p-10 hard-shadow-gold fade-up"
                >
                    <div className="absolute -top-4 -left-4 bg-orange text-navy border-4 border-cream px-3 py-1 font-display uppercase text-xs tracking-widest -rotate-3">
                        Cotiza tu proyecto
                    </div>

                    <div className="space-y-5">
                        <div>
                            <label className="block font-display uppercase tracking-widest text-xs mb-2">
                                Nombre*
                            </label>
                            <input
                                type="text"
                                name="nombre"
                                value={form.nombre}
                                onChange={onChange}
                                placeholder="Tu nombre completo"
                                required
                                data-testid="contact-input-nombre"
                                className="w-full bg-cream text-navy border-4 border-navy px-4 py-3 font-body font-medium placeholder:text-navy/50 focus:outline-none focus:bg-gold/30 transition-colors"
                            />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="block font-display uppercase tracking-widest text-xs mb-2">
                                    Email*
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={onChange}
                                    placeholder="tucorreo@empresa.com"
                                    required
                                    data-testid="contact-input-email"
                                    className="w-full bg-cream text-navy border-4 border-navy px-4 py-3 font-body font-medium placeholder:text-navy/50 focus:outline-none focus:bg-gold/30 transition-colors"
                                />
                            </div>
                            <div>
                                <label className="block font-display uppercase tracking-widest text-xs mb-2">
                                    Teléfono
                                </label>
                                <input
                                    type="tel"
                                    name="telefono"
                                    value={form.telefono}
                                    onChange={onChange}
                                    placeholder="33 1234 5678"
                                    data-testid="contact-input-telefono"
                                    className="w-full bg-cream text-navy border-4 border-navy px-4 py-3 font-body font-medium placeholder:text-navy/50 focus:outline-none focus:bg-gold/30 transition-colors"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block font-display uppercase tracking-widest text-xs mb-2">
                                Mensaje*
                            </label>
                            <textarea
                                name="mensaje"
                                value={form.mensaje}
                                onChange={onChange}
                                placeholder="Cuéntanos sobre tu marca o proyecto..."
                                rows={5}
                                required
                                data-testid="contact-input-mensaje"
                                className="w-full bg-cream text-navy border-4 border-navy px-4 py-3 font-body font-medium placeholder:text-navy/50 focus:outline-none focus:bg-gold/30 transition-colors resize-none"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit-button"
                            className="retro-btn w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2
                                        size={18}
                                        className="animate-spin mr-2"
                                    />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    Enviar mensaje
                                    <Send size={18} className="ml-2" />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}
